import { Check, Droplets, Flame, Wind } from "lucide-react";
import { Reveal } from "./reveal";

const LEVELS = [
  {
    icon: Droplets,
    tag: "Sand Still Hard & Intact",
    title: "Maintenance Wash",
    desc:
      "If the joint sand is still firm with no moss or weeds, we do a controlled low-pressure wash that lifts dirt, algae, and organic buildup without disturbing the sand. It keeps the patio sharp and stretches the time between full re-sanding services.",
    points: [
      "Low-pressure, sand-safe cleaning",
      "Removes surface dirt, algae, and grime",
      "Preserves the existing joint sand",
      "Lower cost — extends the life of your last re-sand",
    ],
  },
  {
    icon: Wind,
    tag: "Moss, Weeds Or Washed-Out Joints",
    title: "Full Joint Blowout & Re-Sand",
    desc:
      "Growth in the joints means the old sand has broken down and is holding moisture — it can't stabilize the pavers anymore. We blow every joint out down to the base, clean the paver faces and joint walls, then install fresh polymeric sand and compact it in.",
    points: [
      "Every joint blown out to the base",
      "Paver faces and joint walls cleaned",
      "Fresh polymeric sand installed",
      "Vibratory compaction to restore interlock",
    ],
  },
];

const POLY_BENEFITS = [
  "Binders harden with water into a firm bond regular sand can't match",
  "Helps hold back weed growth in the joints when maintained yearly",
  "Resists washout during heavy Capital Region rain and snowmelt",
  "Locks pavers together so the surface stops shifting and rocking",
];

const HOT_WATER_BENEFITS = [
  "Heat breaks down grease, oil, BBQ splatter, and sunscreen that cold water just pushes around",
  "Kills algae, moss, and mold at the root instead of only rinsing the surface green away",
  "Cleans deeper at lower pressure, so paver faces and joints don't get etched or blown out",
  "Lifts embedded dirt faster and leaves a more even, uniform color across the whole surface",
  "Surfaces dry quicker, which keeps sanding and sealing on schedule",
];

export function PaverServiceLevels() {
  return (
    <section className="bg-white py-14 md:py-16">
      <div className="mx-auto max-w-[1200px] px-4">
        <div className="text-center mb-8 md:mb-10">
          <p
            className="text-[var(--brand-red)] font-semibold uppercase tracking-[0.2em] text-sm mb-3"
            style={{ fontFamily: "var(--font-display)" }}
          >
            How We Scope Your Job
          </p>
          <h2
            className="text-[var(--brand-deep-blue)] text-3xl md:text-4xl uppercase leading-tight"
            style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
          >
            Maintenance Wash Or Full Re-Sand?
          </h2>
          <p className="mt-3 text-[var(--brand-charcoal)]/85 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            Not every paver surface needs the same service. A lot of jobs we can scope from
            your photos and satellite imagery, and we come out on site when it's needed —
            either way you get an honest recommendation, never a re-sand you don't need.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {LEVELS.map((l, i) => {
            const Icon = l.icon;
            return (
              <Reveal
                key={l.title}
                delayMs={i * 80}
                className="card-pop bg-[var(--brand-light-gray)] rounded-lg p-6 md:p-8 border border-gray-200"
              >
                <div className="w-11 h-11 rounded-md bg-[var(--brand-red)]/10 text-[var(--brand-red)] flex items-center justify-center mb-4">
                  <Icon size={22} />
                </div>
                <p
                  className="text-[var(--brand-red)] text-xs font-bold uppercase tracking-[0.18em] mb-1"
                  style={{ fontFamily: "var(--font-display)" }}
                >
                  {l.tag}
                </p>
                <h3
                  className="text-[var(--brand-deep-blue)] text-xl md:text-2xl uppercase mb-3 leading-tight"
                  style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
                >
                  {l.title}
                </h3>
                <p className="text-[var(--brand-charcoal)]/85 leading-relaxed">{l.desc}</p>
                <ul className="mt-5 space-y-2">
                  {l.points.map((p) => (
                    <li
                      key={p}
                      className="flex items-start gap-2 text-sm text-[var(--brand-charcoal)]/90"
                    >
                      <Check
                        size={16}
                        strokeWidth={3}
                        className="text-[var(--brand-red)] mt-0.5 shrink-0"
                      />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </Reveal>
            );
          })}
        </div>

        <Reveal className="mt-8 rounded-lg bg-[var(--brand-deep-blue)] text-white p-6 md:p-8">
          <h3
            className="text-xl md:text-2xl uppercase leading-tight"
            style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
          >
            Why Polymeric Sand Beats Regular Joint Sand
          </h3>
          <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
            {POLY_BENEFITS.map((b) => (
              <li key={b} className="flex items-start gap-2 text-white/90 text-sm leading-relaxed">
                <Check size={16} strokeWidth={3} className="text-[#C8102E] mt-0.5 shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
          <p className="mt-6 text-white/80 text-sm leading-relaxed border-t border-white/15 pt-5">
            <span className="font-bold text-white">The step most jobs skip:</span> compaction.
            After the sand goes in, we run a vibratory compactor over the pavers so the sand
            settles all the way down into the joints. Skipping it is the number one reason a
            cheap re-sand fails within a season. We also schedule cleaning, sanding, and sealing
            on separate dry days — polymeric sand and sealer both need dry conditions and the
            right temperature to set correctly.
          </p>
          <p className="mt-4 text-white/80 text-sm leading-relaxed">
            <span className="font-bold text-white">Honest note on weeds:</span> polymeric sand
            makes it much harder for weeds to take hold, but seeds blow in and settle on top of
            any patio. With proper care after installation — a yearly maintenance wash — the
            joints stay tight and weed growth stays under control.
          </p>
        </Reveal>

        <Reveal className="mt-6 rounded-lg bg-[var(--brand-light-gray)] border border-gray-200 p-6 md:p-8">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-md bg-[var(--brand-red)]/10 text-[var(--brand-red)] flex items-center justify-center shrink-0">
              <Flame size={22} />
            </div>
            <div>
              <h3
                className="text-[var(--brand-deep-blue)] text-xl md:text-2xl uppercase leading-tight"
                style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
              >
                We Clean With Hot Water Up To 200&deg;F
              </h3>
              <p className="mt-3 text-[var(--brand-charcoal)]/85 text-sm md:text-base leading-relaxed">
                Most companies run cold water off your spigot. We run heated water up to 200&deg;F,
                which does the work that pressure alone can't — without beating up your pavers.
              </p>
              <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                {HOT_WATER_BENEFITS.map((b) => (
                  <li
                    key={b}
                    className="flex items-start gap-2 text-[var(--brand-charcoal)]/85 text-sm leading-relaxed"
                  >
                    <Check size={16} strokeWidth={3} className="text-[var(--brand-red)] mt-0.5 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
